import {
    addDays as addCalendarDays,
    format,
    parseISO,
} from 'date-fns';

export type FacilityStatus =
    | 'onboarding'
    | 'trial'
    | 'trial-expired'
    | 'active'
    | 'not-started';

export interface Facility {
    _id: string;
    name: string;
    isTrial: boolean;
    licenses: number;
    start_date: string | null;
    end_date: string | null;
    region?: string;
    featureAccess?: string[];
    active_licenses: number;
    reportCount?: number;
    admin?: { _id: string; name: string; email: string } | null;
    invitePending?: boolean;
    pendingInviteCount?: number;
    pendingAdminInviteCount?: number;
    userCount?: number;
    readinessScore?: number;
    valueRealizationScore?: number | null;
    address?: {
        street?: string;
        number?: string;
        postalCode: number;
        city: string;
        country: string;
    } | null;
}

/**
 * Derives the lifecycle status of a facility from its Department fields.
 * No backend status field — this is computed on the fly.
 *
 * Go-live depends only on start_date; isTrial only determines what
 * happens AFTER go-live (billing window vs. subscription).
 *
 * Logic:
 *   no start_date            → "not-started"
 *   start_date in the future → "onboarding" (whether trial or subscription)
 *   live + isTrial === false → "active" (direct subscription)
 *   live + within trial end  → "trial"
 *   live + past trial end    → "trial-expired"
 */
export function deriveFacilityStatus(dept: Facility): FacilityStatus {
    const startDate = dept.start_date ? new Date(dept.start_date) : null;
    if (!startDate) return 'not-started';

    const now = new Date();
    if (startDate > now) return 'onboarding';

    if (!dept.isTrial) return 'active';

    const trialEnd = getTrialEndDate(dept.end_date);
    if (!trialEnd || now < trialEnd) return 'trial';
    return 'trial-expired';
}

/**
 * Calculates days remaining until go-live or until trial ends.
 * Returns negative numbers for overdue/expired.
 */
export function getDaysRemaining(
    startDate: string | null,
    type: 'go-live' | 'trial',
    endDate?: string | null
): number | null {
    if (!startDate) return null;
    const start = new Date(startDate);
    const now = new Date();

    if (type === 'go-live') {
        const diffMs = start.getTime() - now.getTime();
        return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    }

    const trialEnd = getTrialEndDate(endDate);
    if (!trialEnd) return null;
    const diffMs = trialEnd.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function getTrialEndDate(endDate?: string | null): Date | null {
    if (!endDate) return null;
    return new Date(endDate);
}

export function isFacilityDeactivated(facility: Facility): boolean {
    if (!facility.end_date) return false;
    return new Date(facility.end_date) <= new Date();
}


// Facility dates are calendar dates stored as UTC-midnight timestamps.
// Parse only the date part so formatting in local time never shifts the day.
function parseCalendarDate(date: string): Date {
    return parseISO(date.slice(0, 10));
}

export function formatDateInputValue(date: Date | string): string {
    const d = typeof date === 'string' ? parseCalendarDate(date) : date;
    return format(d, 'yyyy-MM-dd');
}

export function formatFacilityDisplayDate(date?: string | null): string {
    if (!date) return 'Not set';

    return format(parseCalendarDate(date), 'MMM d, yyyy');
}

export function getFacilityAdoptionPercent(facility: Facility): number {
    if (facility.licenses <= 0) return 0;

    return Math.round((facility.active_licenses / facility.licenses) * 100);
}

export function hasPendingSiteAdminInvitation(facility: Facility): boolean {
    return Boolean(
        (facility.pendingAdminInviteCount &&
            facility.pendingAdminInviteCount > 0) ||
            facility.invitePending
    );
}


export function formatFacilityAddress(address?: Facility['address']): string {
    if (!address) return '';

    const streetLine = [address.street, address.number]
        .filter(Boolean)
        .join(' ');
    const cityLine = [address.postalCode, address.city]
        .filter(Boolean)
        .join(' ');

    return [streetLine, cityLine, address.country].filter(Boolean).join(', ');
}

export function addDays(dateStr: string, days: number): string {
    return formatDateInputValue(addCalendarDays(parseCalendarDate(dateStr), days));
}
